import { Button } from '@kvdbil/components';
import { ButtonProps } from '@kvdbil/components/types/components/Button';
import { Color } from '@kvdbil/components/types/Types';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { releasingEnabled } from '~/config/public/environment';
import Link from '~/App/shared/components/Link';
import CompletedCheckoutStatus from '~/App/views/Checkout/components/CompletedCheckoutStatus';
import ReleasingCost from './ReleasingCost';
import { useTranslation } from '~/Locale';

const BiddingMessageContainer = styled.div<{ color?: Color }>`
  border: 1.5px solid
    ${props =>
      props.color
        ? props.theme.colors[props.color].mainAlt
        : props.theme.colors.grayscaleToned.dark3};
  border-radius: 5px;
  width: 100%;
  margin-top: 90px;
  padding: 0 32px 32px;
`;

const BiddingMessageIcon = styled.div<{ color?: Color }>`
  width: 116px;
  height: 116px;
  border: 1.5px solid
    ${props =>
      props.color
        ? props.theme.colors[props.color].mainAlt
        : props.theme.colors.grayscaleToned.dark3};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props =>
    props.color
      ? props.theme.colors[props.color].mainAlt
      : props.theme.colors.grayscaleToned.dark3};
  margin: -58px auto 20px;
  background: ${({ theme }) => theme.colors.background.light};
`;

const BiddingMessageHeading = styled.h3`
  font-size: 1.25em;
  color: ${({ theme }) => theme.colors.text.dark};
  text-align: center;
  font-weight: normal;
  margin-bottom: 12px;
`;

const BiddingMessageLabel = styled.div`
  font-size: 0.75em;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
  text-align: center;
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  text-transform: uppercase;
`;

const BiddingMessageTitle = styled.p`
  font-weight: 500;
  font-size: 1em;
  color: ${({ theme }) => theme.colors.text.dark};
  margin-top: 2em;
  margin-bottom: 0.5em;
`;

const BiddingMessageValue = styled.div<{ color?: Color }>`
  color: ${props =>
    props.color
      ? props.theme.colors[props.color].mainAlt
      : props.theme.colors.grayscaleToned.dark3};
  font-weight: 500;
  font-size: 2em;
  text-align: center;
`;

const BiddingMessageCurrency = styled.span`
  font-size: 1rem;
`;

const BiddingMessageText = styled.p`
  font-size: 1em;
  color: ${({ theme }) => theme.colors.text.dark};
  margin-bottom: 1rem;
  font-weight: ${({ theme }) => theme.typography.fontBaseRegularWeight};
`;

export interface BiddingCardProps {
  color?: Color;
  heading: string;

  checkoutOrderId?: number;
  isCheckoutComplete?: boolean;

  price: {
    amount: string;
    currency: string;
  };

  label: string;

  icon: ReactNode;

  additionalInformation?: {
    title: string;
    text: string;
  }[];

  buttonUrl?: string;
  buttonText?: string;

  releasingCost?: number;
  buttonProps?: Pick<ButtonProps, 'color' | 'size' | 'variant'>;
  hasBids: boolean;
}

export const BiddingCard = ({
  color,
  heading,
  label,
  hasBids,
  price,
  releasingCost,
  additionalInformation,
  icon,
  isCheckoutComplete,
  buttonText,
  buttonUrl,
  buttonProps,
  checkoutOrderId
}: BiddingCardProps) => {
  const { t } = useTranslation();

  return (
    <BiddingMessageContainer color={color}>
      <BiddingMessageIcon color={color}>{icon}</BiddingMessageIcon>
      <BiddingMessageHeading>{heading}</BiddingMessageHeading>

      {hasBids && <BiddingMessageLabel>{label}</BiddingMessageLabel>}
      <BiddingMessageValue color={color}>
        {hasBids ? price.amount : t('No bids')}
        {hasBids && (
          <BiddingMessageCurrency>{price.currency}</BiddingMessageCurrency>
        )}
      </BiddingMessageValue>
      {releasingEnabled && (releasingCost ?? 0) > 0 && (
        <ReleasingCost cost={releasingCost || 0} />
      )}
      {(additionalInformation || []).map(information => (
        <div key={information.title}>
          <BiddingMessageTitle>{information.title}</BiddingMessageTitle>
          <BiddingMessageText>{information.text}</BiddingMessageText>
        </div>
      ))}
      {!isCheckoutComplete && buttonText && (
        <>
          <Link to={buttonUrl}>
            <Button color="secondary" fullWidth size="regular" {...buttonProps}>
              {buttonText}
            </Button>
          </Link>
        </>
      )}
      {checkoutOrderId && isCheckoutComplete && (
        <CompletedCheckoutStatus orderId={checkoutOrderId} />
      )}
    </BiddingMessageContainer>
  );
};

export default BiddingCard;
